import React, { useRef, useState } from 'react';
import { formatShortDate } from '@utils/datetime';
import { getLocationLabel } from '@utils/eventLocation';
import { logger } from '@utils/logger';
import { isAxiosError } from 'axios';
import { Calendar, MapPin, Info, Share } from 'lucide-react';
import { CalendarBlankIcon } from '@components/icons/CalendarBlankIcon';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { ImgWithFallback } from './ui/img-with-fallback';
import {
  useGetMyCheckedInEventsApiV1EventsMyCheckedInGet,
  useUpdateQuestionApiV1QuestionsQuestionIdPut,
} from '@api-client/api-client';
import { useAuth } from '@providers';
import { useToast } from '@hooks/use-toast';
import type { EventRead } from '@models/api';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import { crosspostedToast } from '@utils/heldPostCopy';
import { classifyCrosspostOutcome } from '@utils/heldSpaces';
import { retryUnlessClientError } from '@lib/mutationRetry';
import { readingTimeMs } from '@lib/toastDurations';

interface CrosspostModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  question: {
    id: string;
    title: string;
    content: string;
    is_anonymous: boolean;
    is_featured: boolean;
    is_official: boolean;
    events: { id: string | null; name: string }[];
  };
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Modal for crossposting a question to other events.
 */
export const CrosspostModal: React.FC<CrosspostModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  question,
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { trackEvent } = useAmplitudeTracking();
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [showHelp, setShowHelp] = useState(false);

  // Fetch user's checked-in events (excluding Meetverse and current event)
  const {
    data: checkedInEventsData,
    isLoading,
    error,
  } = useGetMyCheckedInEventsApiV1EventsMyCheckedInGet(
    false, // Don't include Meetverse for crosspost
    { enabled: isOpen && !!user?.id },
  );

  // Filter out all current events the question is already posted in
  const currentEventIds = new Set(question.events.map(e => e.id).filter(Boolean));
  const availableEvents = (checkedInEventsData?.data || []).filter(
    (event: EventRead) => !currentEventIds.has(event.id),
  );
  // What Confirm would send: the picked Events the post is not in yet. A picked Event can be
  // posted already (the page read the post again after a save whose answer was lost), and it is
  // then neither counted on the button nor sent again.
  const chosenEvents = availableEvents.filter(event => selectedEvents.includes(event.id));
  // The Events of the save in flight, kept from the tap that sent it. A pending mutation runs
  // the LATEST render's onSuccess, and the save's own realtime echo can re-render this dialog
  // with those Events already in `question.events`, so they are no longer in `availableEvents`
  // by the time the answer lands. The generated hook's variables type is closed, so this ref
  // stands in for passing them through the mutation's variables. The success and failure
  // events report it too: what was sent, not every card that was picked.
  const sentEventsRef = useRef<{ id: string; name: string }[]>([]);

  const updateQuestionMutation = useUpdateQuestionApiV1QuestionsQuestionIdPut({
    // A 4xx answer is final: the same save gets the same refusal, and it is said at once.
    retry: retryUnlessClientError,
    onSuccess: response => {
      trackEvent(ANALYTICS_EVENTS.post_crosspost_succeeded, {
        user_id: user?.id ?? null,
        question_id: question.id,
        is_anonymous: question.is_anonymous,
        selected_event_ids: sentEventsRef.current.map(event => event.id),
        selected_event_count: sentEventsRef.current.length,
      });
      // Worded from the server's answer, not from what was asked for. A chosen Event can wait:
      // when the post is waiting in a Space that hosts it, the save still answers 200 and the
      // Event is not among the answer's Events. The chosen Events are the ones the tap sent,
      // so neither a card toggled while the save was in flight nor a re-read of the post
      // changes what is said. It names the Events, so it stays up as long as they take to read.
      const crossposted = crosspostedToast(
        classifyCrosspostOutcome(sentEventsRef.current, response.data),
      );
      toast({
        ...crossposted,
        duration: readingTimeMs(crossposted.title, crossposted.description),
      });
      onSuccess?.();
      onClose();
      setSelectedEvents([]);
    },
    onError: error => {
      logger.error('Error crossposting question:', error);

      const errorMessage = isAxiosError(error)
        ? (() => {
            const data = error.response?.data as unknown;
            if (isRecord(data) && typeof data.detail === 'string') {
              return data.detail;
            }
            return error.message;
          })()
        : error instanceof Error
          ? error.message
          : 'unknown';

      trackEvent(ANALYTICS_EVENTS.post_crosspost_failed, {
        user_id: user?.id ?? null,
        question_id: question.id,
        is_anonymous: question.is_anonymous,
        selected_event_ids: sentEventsRef.current.map(event => event.id),
        selected_event_count: sentEventsRef.current.length,
        error_message: errorMessage,
      });
      // The dialog stays open with the selection, so the next step is the same button again.
      toast({
        title: "Couldn't crosspost your post",
        description: 'Try again in a moment.',
        variant: 'error',
        duration: 5000,
      });
    },
  });

  // Helper function to check if event is upcoming
  const isEventUpcoming = (endDate: string) => {
    return new Date(endDate) >= new Date();
  };

  // Separate events into upcoming and past for section display
  const upcomingEvents = availableEvents.filter((event: EventRead) =>
    isEventUpcoming(event.end_date || event.start_date),
  );
  const pastEvents = availableEvents.filter(
    (event: EventRead) => !isEventUpcoming(event.end_date || event.start_date),
  );

  const handleEventToggle = (eventId: string) => {
    trackEvent(ANALYTICS_EVENTS.post_crosspost_event_toggled, {
      user_id: user?.id ?? null,
      question_id: question.id,
      is_anonymous: question.is_anonymous,
      event_id: eventId,
      is_selected: !selectedEvents.includes(eventId),
    });

    setSelectedEvents(prev => {
      if (prev.includes(eventId)) {
        return prev.filter(id => id !== eventId);
      } else {
        return [...prev, eventId];
      }
    });
  };

  const handleConfirm = () => {
    if (chosenEvents.length === 0 || !user?.id) return;

    trackEvent(ANALYTICS_EVENTS.post_crosspost_submit_clicked, {
      user_id: user.id,
      question_id: question.id,
      is_anonymous: question.is_anonymous,
      selected_event_ids: chosenEvents.map(event => event.id),
      selected_event_count: chosenEvents.length,
    });

    const existingEventIds = question.events.map(e => e.id).filter(Boolean) as string[];
    const droppedCount = question.events.length - existingEventIds.length;
    if (droppedCount > 0) {
      logger.warn(`CrosspostModal: ${droppedCount} event(s) had null IDs and were excluded`);
    }
    const allEventIds = [...existingEventIds, ...chosenEvents.map(event => event.id)];
    const uniqueEventIds = [...new Set(allEventIds)];

    sentEventsRef.current = chosenEvents.map(({ id, name }) => ({ id, name }));
    updateQuestionMutation.mutate({
      questionId: question.id,
      data: {
        event_ids: uniqueEventIds,
      },
    });
  };

  const renderEventCard = (event: EventRead, isUpcoming: boolean) => {
    const isSelected = selectedEvents.includes(event.id);
    // URL locations (calendar imports) show as their platform; '' drops the row and its map pin.
    const locationLabel = getLocationLabel(event.location);

    return (
      <Card
        key={event.id}
        className={`transition-all duration-200 rounded-2xl cursor-pointer hover:shadow-lg ${
          isSelected
            ? 'border-4 border-amber-400 bg-amber-50'
            : isUpcoming
              ? 'border-2 border-gray-200 bg-white hover:border-gray-300'
              : 'border-2 border-dashed border-gray-300 bg-gray-50 hover:border-gray-400 opacity-80'
        }`}
        onClick={() => {
          handleEventToggle(event.id);
        }}
      >
        <CardContent className="p-4">
          {/* Event Name */}
          <h3
            className={`font-semibold text-sm ${isUpcoming ? 'text-gray-900' : 'text-gray-600'} truncate mb-3`}
          >
            {event.name}
          </h3>

          <div className="flex items-start gap-3">
            {/* Upcoming Badge */}
            {isUpcoming && (
              <div className="shrink-0">
                <div
                  className="bg-blue-100 text-blue-700 text-[10px] font-medium px-0.5 py-1.5 rounded-full"
                  style={{
                    writingMode: 'vertical-rl',
                    transform: 'rotate(180deg)',
                  }}
                >
                  Upcoming
                </div>
              </div>
            )}

            {/* Event Image — the outer guard is load-bearing: an event with no
                image_url renders no tile at all, as it always has. The fallback
                below is for a URL that is present but fails to load. */}
            {event.image_url && (
              <div className="w-14 h-14 rounded-xl overflow-hidden shrink-0 shadow-xs">
                <ImgWithFallback
                  src={event.image_url}
                  alt={event.name}
                  className="w-full h-full object-cover"
                  fallback={() => (
                    <div className="flex h-full w-full items-center justify-center bg-mb-beige text-mb-gray-text">
                      <CalendarBlankIcon className="h-6 w-6" />
                    </div>
                  )}
                />
              </div>
            )}

            {/* Event Details */}
            <div className="flex-1 min-w-0">
              <div className="space-y-1.5">
                {/* Date — in the viewer's own zone: this modal is a list surface
                    (design.md, "Event lists use the viewer's timezone"). */}
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Calendar className="w-3 h-3" />
                  <span>{formatShortDate(event.start_date)}</span>
                </div>

                {/* Location */}
                {locationLabel && (
                  <div className="flex items-start gap-2 text-xs text-gray-500">
                    <MapPin className="w-3 h-3 shrink-0 mt-0.5" />
                    <span className="wrap-break-word">{locationLabel}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Selection Indicator */}
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 shrink-0">
              {isSelected ? (
                <div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center">
                  <span className="text-mb-ink text-xs font-bold">✓</span>
                </div>
              ) : (
                <div className="w-3 h-3 rounded-full border-2 border-gray-400"></div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-w-md w-[90vw] max-h-[85vh] flex flex-col bg-amber-50 [--mb-surface:var(--color-amber-50)] border-amber-200 rounded-2xl p-4">
        <DialogHeader>
          <div className={`transition-all duration-300 ${showHelp ? 'mb-4' : ''}`}>
            <div className="flex items-center gap-3">
              <button
                type="button"
                aria-label="About crossposting"
                aria-expanded={showHelp}
                onClick={() => {
                  setShowHelp(!showHelp);
                }}
                className={`w-7 h-7 rounded-full bg-amber-100 hover:bg-amber-200 flex items-center justify-center transition-all duration-300 ${showHelp ? 'ring-2 ring-amber-300' : ''}`}
              >
                <Info className="w-4 h-4 text-amber-700" aria-hidden="true" />
              </button>
              <DialogTitle className="text-gray-800">Crosspost to Other Events</DialogTitle>
            </div>
            {showHelp && (
              <div className="mt-3 p-3 bg-amber-100 rounded-lg border border-amber-200 transition-all duration-300 ring-2 ring-amber-300">
                <p className="text-sm text-amber-800">
                  Share this post with additional events. Select the events where you'd like this
                  post to appear.
                </p>
              </div>
            )}
          </div>

          {/* Current Event Info */}
          {question.events.length > 0 && (
            <div className="bg-amber-100 rounded-lg p-3 border border-amber-200">
              <p className="text-sm font-medium text-amber-900 mb-1">Currently posted in:</p>
              <ul className="text-sm text-amber-700 space-y-0.5">
                {question.events.map((e, i) => (
                  <li key={e.id ?? i}>{e.name}</li>
                ))}
              </ul>
            </div>
          )}

          <DialogDescription className="sr-only">
            Share this post with additional events.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center py-8">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
              <p className="text-sm text-gray-600">Loading events</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex-1 flex items-center justify-center py-8">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
                <span className="text-destructive text-lg">⚠️</span>
              </div>
              <p className="text-sm text-gray-600">Failed to load events</p>
              <p className="text-xs text-gray-500 mt-1">Please try again</p>
            </div>
          </div>
        ) : availableEvents.length === 0 ? (
          <div className="flex-1 flex items-center justify-center py-8">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                <Share className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-sm text-gray-600 font-medium mb-1">No other events available</p>
              <p className="text-xs text-gray-500">
                You're not checked into any other events to crosspost to.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 max-h-[50vh] overflow-y-auto">
              <div className="space-y-3">
                {/* Upcoming Events Section */}
                {upcomingEvents.length > 0 && (
                  <>
                    <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider px-1 py-2">
                      Upcoming Events
                    </div>
                    {upcomingEvents.map(event => renderEventCard(event, true))}
                  </>
                )}

                {/* Past Events Section */}
                {pastEvents.length > 0 && (
                  <>
                    <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider px-1 py-2">
                      Past Events
                    </div>
                    {pastEvents.map(event => renderEventCard(event, false))}
                  </>
                )}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-100 rounded-xl"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={chosenEvents.length === 0 || updateQuestionMutation.isPending}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-mb-ink rounded-xl disabled:bg-amber-300"
              >
                {updateQuestionMutation.isPending ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-mb-ink border-t-transparent rounded-full animate-spin"></div>
                    Crossposting
                  </div>
                ) : (
                  `Crosspost to ${chosenEvents.length} event${chosenEvents.length !== 1 ? 's' : ''}`
                )}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
